import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BusinessIcon from '@mui/icons-material/Business';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Badge } from '@tinyrewards/shared';
import { getBadge } from '../api/badges';
import { PURPLE_AVATAR } from '../theme/tokens';

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="overline" color="text.secondary" lineHeight={1.2}>
                {label}
            </Typography>
            <Box mt={0.5}>{children}</Box>
        </Box>
    );
}

export default function BadgeDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [badge, setBadge] = useState<Badge | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        getBadge(id)
            .then((res) => {
                if (res.success && res.data) {
                    setBadge(res.data);
                } else {
                    setError(res.message ?? 'Badge not found.');
                }
            })
            .catch(() => setError('Failed to load badge.'))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !badge) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/badges')} sx={{ mb: 2 }}>
                    Back to Manage Badges
                </Button>
                <Alert severity="error">{error ?? 'Badge not found.'}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box display="flex" gap={1} sx={{ mb: 3 }}>
                <Button color="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/badges')}>
                    Back to Manage Badges
                </Button>
                <Button
                    startIcon={<EditIcon />}
                    component={Link}
                    to={`/badges/${id}/edit`}
                >
                    Edit Badge
                </Button>
            </Box>

            {/* Hero — image + title + issuer */}
            <Paper sx={{ overflow: 'hidden', mb: 3 }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor={PURPLE_AVATAR}
                    sx={{ p: 4, minHeight: 240 }}
                >
                    {badge.badgeImage ? (
                        <Box
                            component="img"
                            src={badge.badgeImage}
                            alt={badge.badgeName}
                            sx={{ maxHeight: 200, maxWidth: '100%', width: 'auto', objectFit: 'contain' }}
                        />
                    ) : (
                        <WorkspacePremiumIcon sx={{ fontSize: 120, color: 'rgba(255,255,255,0.3)' }} />
                    )}
                </Box>

                <Box sx={{ p: 3 }}>
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                        {badge.badgeName}
                    </Typography>

                    {badge.issuerName && (
                        <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
                            <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body1" color="text.secondary">
                                {badge.issuerName}
                            </Typography>
                        </Box>
                    )}

                    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                        <Chip
                            label={badge.isPublic ? 'Public' : 'Private'}
                            color={badge.isPublic ? 'success' : 'default'}
                            size="small"
                        />
                        <Chip
                            label={badge.isActive ? 'Active' : 'Inactive'}
                            color={badge.isActive ? 'primary' : 'error'}
                            size="small"
                        />
                        {(badge.badgeType === 'watch' || (badge.badgeType === 'Default' && badge.targetUrl && /youtube\.com|youtu\.be/.test(badge.targetUrl))) && (
                            <Chip label="Watch to Earn" color="warning" size="small" icon={<PlayCircleOutlineIcon />} />
                        )}
                    </Box>

                    {/* Earn Now + YouTube presentation → in-app viewer */}
                    {badge.badgeType === 'Default' && badge.targetUrl && /youtube\.com|youtu\.be/.test(badge.targetUrl) && (
                        <Box mt={2}>
                            <Button
                                component={Link}
                                to={`/watch/${badge.badgeID}`}
                                variant="contained"
                                startIcon={<PlayCircleOutlineIcon />}
                                color="warning"
                            >
                                Watch to Earn
                            </Button>
                        </Box>
                    )}

                    {/* Watch-to-Earn → open YouTube directly */}
                    {badge.badgeType === 'watch' && badge.targetUrl && (
                        <Box mt={2}>
                            <Button
                                component="a"
                                href={badge.targetUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="contained"
                                startIcon={<PlayCircleOutlineIcon />}
                                color="warning"
                            >
                                Watch on YouTube
                            </Button>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Details */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    Badge Details
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box display="flex" flexDirection="column" gap={3}>
                    <DetailRow label="Description">
                        {badge.badgeDescription ? (
                            <Typography
                                variant="body1"
                                component="div"
                                dangerouslySetInnerHTML={{ __html: badge.badgeDescription }}
                                sx={{ '& p': { mt: 0, mb: 1 }, '& p:last-child': { mb: 0 } }}
                            />
                        ) : (
                            <Typography variant="body1" color="text.disabled">—</Typography>
                        )}
                    </DetailRow>

                    <Divider />

                    <DetailRow label="Type">
                        <Typography variant="body1" color="text.disabled">—</Typography>
                    </DetailRow>

                    <Divider />

                    <DetailRow label="Criteria">
                        {badge.badgeCriteria ? (
                            <Typography
                                variant="body1"
                                component="div"
                                dangerouslySetInnerHTML={{ __html: badge.badgeCriteria }}
                                sx={{ '& p': { mt: 0, mb: 1 }, '& p:last-child': { mb: 0 } }}
                            />
                        ) : (
                            <Typography variant="body1" color="text.disabled">—</Typography>
                        )}
                    </DetailRow>

                    <Divider />

                    <DetailRow label="Expiration">
                        <Typography variant="body1" color="text.disabled">—</Typography>
                    </DetailRow>

                    <Divider />

                    <DetailRow label="Audience">
                        <Typography variant="body1">
                            {badge.isPublic ? 'Public — anyone can discover and earn this badge' : 'Private — only invited recipients can earn this badge'}
                        </Typography>
                    </DetailRow>
                </Box>
            </Paper>
        </Container>
    );
}
